<template>
  <div class="border border-slate-200 bg-white p-4 shadow-sm">
    <div class="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-4 sm:gap-y-2">
      <h3 class="shrink-0 text-base font-semibold leading-tight text-slate-800 sm:py-1">
        {{ t('sensor.door_open_chart.filtered_period_title') }}
      </h3>
      <div class="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2">
        <label class="flex shrink-0 items-center gap-1.5 px-3 text-sm text-slate-600">
          {{ t('sensor.door_open_chart.resolution') }}
          <select
            v-model="selectedResolution"
            class="border border-slate-300 bg-white pl-3 pr-8 py-1.5 text-sm text-left text-slate-700 transition-colors hover:bg-slate-50 hover:border-slate-400"
            :title="t('sensor.door_open_chart.resolution_hint')">
            <option
              v-for="opt in availableResolutions"
              :key="opt.value"
              :value="opt.value">
              {{ opt.label }}
            </option>
          </select>
        </label>
        <div class="min-w-0 shrink-0">
          <TimeRangeFilter default-preset="24h" @change="onRangeChange" />
        </div>
        <button
          type="button"
          class="flex shrink-0 items-center gap-1.5 border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-700 transition-colors hover:bg-slate-50 hover:border-slate-400"
          :title="t('sensor.door_open_chart.maximize_title')"
          @click="openFullscreen">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
          </svg>
          {{ t('sensor.door_open_chart.maximize') }}
        </button>
      </div>
    </div>

    <SensorChartAlarmCards
      v-model:expanded="alarmsSectionExpanded"
      :alarms="alarmList"
      :is-loading="isAlarmsLoading"
      accent="orange"
      @select-alarm="openAlarmDetail"
      @hover-alarm="onAlarmHover" />

    <!-- Gráfico State Timeline -->
    <div ref="chartContainer" class="h-64 w-full min-h-[16rem]" />
    <p v-if="isLoading && !hasChartRange" class="mt-2 text-center text-sm text-slate-500">
      {{ t('sensor.door_open_chart.loading') }}
    </p>
    <p v-else-if="!isLoading && !hasChartRange" class="mt-2 text-center text-sm text-slate-500">
      {{ t('sensor.door_open_chart.no_data_in_period') }}
    </p>

    <div
      v-if="!isLoading && sensorAlarms.length > 0"
      class="mt-3 flex flex-wrap gap-3 text-xs text-slate-600">
      <span class="flex items-center gap-1.5">
        <span class="inline-block h-3 w-5 border border-orange-400 bg-orange-300/40" />
        {{ t('sensor.door_open_chart.alarm_registered') }}
      </span>
    </div>

    <p
      v-if="!isLoading && !hasDoorOpenAlarmConfig && shortOpenPeriods.length > 0"
      class="mt-2 text-xs text-slate-500">
      {{ t('sensor.door_open_chart.configure_alarm_profile_hint') }}
    </p>

    <!-- Períodos alarmáveis (open_periods) -->
    <section v-if="alarmWorthyPeriods.length > 0" class="mt-6">
      <div class="mb-3 flex flex-wrap items-center gap-2">
        <h4 class="text-sm font-semibold text-slate-800">{{ t('sensor.door_open_chart.openings_above_setpoint') }}</h4>
        <span
          v-if="maxDoorOpenTimeSetpoint != null"
          class="border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-800">
          {{ t('sensor.door_open_chart.setpoint_minutes', { minutes: maxDoorOpenTimeSetpoint }) }}
        </span>
      </div>
      <DoorOpenPeriodsTable
        :periods="alarmWorthyPeriods"
        :show-seconds="showSeconds"
        :format-date-time="formatDateTime"
        :format-period-duration="formatAlarmWorthyPeriodDuration"
      />
    </section>

    <!-- Aberturas curtas ou todas (time_door_open) -->
    <section v-if="shortOpenPeriods.length > 0" class="mt-6">
      <h4 class="mb-3 text-sm font-semibold text-slate-800">
        {{
          hasDoorOpenAlarmConfig
            ? t('sensor.door_open_chart.short_openings')
            : t('sensor.door_open_chart.detected_openings')
        }}
      </h4>
      <DoorOpenPeriodsTable :periods="shortOpenPeriods" :show-seconds="showSeconds" :format-date-time="formatDateTime" :format-period-duration="formatPeriodDuration" />
    </section>
  </div>

  <!-- Overlay tela cheia -->
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="isFullscreen"
        class="fixed inset-0 z-[100000] flex flex-col bg-white"
        role="dialog"
        aria-modal="true"
        :aria-label="t('sensor.door_open_chart.fullscreen_aria')">
        <div class="flex flex-none flex-col gap-4 border-b border-slate-200 bg-slate-50 px-4 py-3">
          <div class="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-4 sm:gap-y-2">
            <h3 class="shrink-0 text-lg font-semibold leading-tight text-slate-800 sm:py-1">
              {{ t('sensor.door_open_chart.filtered_period_title') }}
            </h3>
            <div class="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2">
              <label class="flex shrink-0 items-center gap-1.5 px-3 text-sm text-slate-600">
                {{ t('sensor.door_open_chart.resolution') }}
                <select
                v-model="selectedResolution"
                class="border border-slate-300 bg-white pl-3 pr-8 px-5 py-1.5 text-sm text-left text-slate-700 transition-colors hover:bg-slate-50 hover:border-slate-400"
                :title="t('sensor.door_open_chart.resolution_hint')">
                <option
                    v-for="opt in availableResolutions"
                    :key="opt.value"
                    :value="opt.value">
                    {{ opt.label }}
                  </option>
                </select>
              </label>
              <div class="min-w-0 shrink-0">
                <TimeRangeFilter :initial-range="currentRange" default-preset="24h" @change="onRangeChange" />
              </div>
              <button
                type="button"
                class="flex shrink-0 items-center gap-1.5 border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-700 transition-colors hover:bg-red-50 hover:border-red-300 hover:text-red-700"
                :title="t('sensor.door_open_chart.close_fullscreen_title')"
                @click="closeFullscreen">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                {{ t('sensor.door_open_chart.close_fullscreen') }}
              </button>
            </div>
          </div>
        </div>
        <div class="flex flex-1 flex-col overflow-auto p-4">
          <div ref="chartContainerFullscreen" class="min-h-[28rem] flex-1 w-full" />

          <section v-if="alarmWorthyPeriods.length > 0" class="mt-6">
            <div class="mb-3 flex flex-wrap items-center gap-2">
              <h4 class="text-sm font-semibold text-slate-800">{{ t('sensor.door_open_chart.openings_above_setpoint') }}</h4>
              <span
                v-if="maxDoorOpenTimeSetpoint != null"
                class="border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-800">
                {{ t('sensor.door_open_chart.setpoint_minutes', { minutes: maxDoorOpenTimeSetpoint }) }}
              </span>
            </div>
            <DoorOpenPeriodsTable
              :periods="alarmWorthyPeriods"
              :show-seconds="showSeconds"
              :format-date-time="formatDateTime"
              :format-period-duration="formatAlarmWorthyPeriodDuration"
            />
          </section>

          <section v-if="shortOpenPeriods.length > 0" class="mt-6">
            <h4 class="mb-3 text-sm font-semibold text-slate-800">
              {{
                hasDoorOpenAlarmConfig
                  ? t('sensor.door_open_chart.short_openings')
                  : t('sensor.door_open_chart.detected_openings')
              }}
            </h4>
      <DoorOpenPeriodsTable :periods="shortOpenPeriods" :show-seconds="showSeconds" :format-date-time="formatDateTime" :format-period-duration="formatPeriodDuration" />
          </section>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { TimeRangeFilter } from '@/components/Globals/TimeRangeFilter'
import type { TimeRangeValue } from '@/components/Globals/TimeRangeFilter'
import * as echarts from 'echarts'
import { useDoorOpenChartStore } from '@/store/doorOpenChart'
import { useSensorDoorOpenChart } from '@/composables/useSensorDoorOpenChart'
import DoorOpenPeriodsTable from '@/components/Sensors/DoorOpenPeriodsTable.vue'
import SensorChartAlarmCards from '@/components/Sensors/SensorChartAlarmCards.vue'
import type { ISensorChartOverlayAlarm } from '@/interfaces/components/sensorChartAlarm'
import {
  fetchSensorChartOverlayAlarms,
  filterChartOverlayAlarms,
  resolveChartHoverIndex,
} from '@/utils/sensor-chart-alarms'
import AlarmResource from '@/api/resources/alarm'
import {
  buildShortOpenPeriods,
  hasDoorOpenAlarmConfig as checkDoorOpenAlarmConfig,
  parseTimeDoorOpen,
  periodDurationMinutes,
  type IDoorOpenPeriod,
} from '@/utils/door-open-periods'
import { useI18n } from '@/composable/useI18n'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

const { t } = useI18n()
const router = useRouter()

const props = defineProps<{
  sensorId: number
  alarmDoorOpenProfileId?: number | null
}>()

const chartContainer = ref<HTMLDivElement | null>(null)
const chartContainerFullscreen = ref<HTMLDivElement | null>(null)
let chartInstance: echarts.ECharts | null = null
const isFullscreen = ref(false)
const alarmsSectionExpanded = ref(true)
const hoveredAlarmIndex = ref<number | null>(null)
const savedDataZoom = ref({ start: 0, end: 100 })
const currentRange = ref<TimeRangeValue | null>(null)

const isLoading = ref(false)
const isAlarmsLoading = ref(false)
const hasFetched = ref(false)

const RESOLUTION_OPTIONS = [
  { value: '15m', label: '15m', maxDays: Infinity },
  { value: '1m', label: '1m', maxDays: Infinity },
  { value: '30s', label: '30s', maxDays: 2 },
  { value: '15s', label: '15s', maxDays: 2},
  { value: '10s', label: '10s', maxDays: 2 },
  { value: '5s', label: '5s', maxDays: 2 },
  { value: '1s', label: '1s', maxDays: 2}
] as const

const selectedResolution = ref('1m')

const allowsSubMinuteResolution = computed(() => {
  if (!currentRange.value) return false
  const hours = (currentRange.value.end.getTime() - currentRange.value.start.getTime()) / (60 * 60 * 1000)
  return hours <= 48
})

const availableResolutions = computed(() =>
  RESOLUTION_OPTIONS.filter((o) => o.maxDays !== 2 || allowsSubMinuteResolution.value),
)

function defaultResolutionForRange(range: TimeRangeValue): string {
  const hours = (range.end.getTime() - range.start.getTime()) / (60 * 60 * 1000)
  if (hours > 168) return '15m'
  if (hours > 48) return '1m'
  return '30s'
}

interface DoorOpenPeriodResponse {
  open_periods?: IDoorOpenPeriod[]
}

const maxDoorOpenTimeSetpoint = ref<number | null>(null)
const debounceTimeSetpoint = ref<number | null>(null)
const alarmWorthyPeriods = ref<IDoorOpenPeriod[]>([])
const shortOpenPeriods = ref<IDoorOpenPeriod[]>([])

const hasDoorOpenAlarmConfig = computed(() =>
  checkDoorOpenAlarmConfig(maxDoorOpenTimeSetpoint.value, debounceTimeSetpoint.value),
)

const alarmList = ref<ISensorChartOverlayAlarm[]>([])

const sensorAlarms = computed(() => filterChartOverlayAlarms(alarmList.value))

const alarmPeriodsMs = computed(() => {
  if (!currentRange.value) return []
  const rangeEndMs = currentRange.value.end.getTime()
  return sensorAlarms.value.map((a) => {
    const start = new Date(a.start_date).getTime()
    const end = a.end_date ? new Date(a.end_date).getTime() : rangeEndMs
    return [start, end] as [number, number]
  })
})

/** Todos os períodos de porta aberta (alarmáveis + curtos) para o State Timeline. */
const doorOpenPeriodsMs = computed(() =>
  periodsToMs([...alarmWorthyPeriods.value, ...shortOpenPeriods.value]),
)

function periodsToMs(periods: IDoorOpenPeriod[]): [number, number][] {
  if (!currentRange.value) return []
  const rangeEndMs = currentRange.value.end.getTime()
  return periods.map((p) => {
    const start = new Date(p.start_date).getTime()
    const end = p.end_date ? new Date(p.end_date).getTime() : rangeEndMs
    return [start, end] as [number, number]
  })
}

const rangeStartMs = computed(() => currentRange.value?.start.getTime() ?? null)
const rangeEndMs = computed(() => currentRange.value?.end.getTime() ?? null)
const hasChartRange = computed(() => {
  const start = rangeStartMs.value
  const end = rangeEndMs.value
  return hasFetched.value && start != null && end != null && end > start
})
const chartRowLabel = computed(() => t('sensor.door_open_chart.chart_row_label'))

const showSeconds = computed(() => selectedResolution.value.endsWith('s'))

const dateTimeFormatOptions = computed<Intl.DateTimeFormatOptions>(() => ({
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  ...(showSeconds.value ? { second: '2-digit' as const } : {}),
}))

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return t('sensor.door_open_chart.open_ended')
  const d = new Date(dateStr)
  return d.toLocaleString('pt-BR', dateTimeFormatOptions.value)
}

function formatDuration(startStr: string | null, endStr: string | null): string {
  if (!startStr || !endStr) return ''
  const start = new Date(startStr).getTime()
  const end = new Date(endStr).getTime()
  const diffMs = Math.max(0, end - start)
  const totalSec = Math.floor(diffMs / 1000)
  const totalMin = Math.floor(totalSec / 60)
  const seconds = totalSec % 60
  const d = Math.floor(totalMin / 1440)
  const restMin = totalMin % 1440
  const h = Math.floor(restMin / 60)
  const m = restMin % 60
  if (d > 0) {
    return showSeconds.value
      ? t('sensor.door_open_chart.duration_days_hours_minutes_seconds', { days: d, hours: h, minutes: m, seconds })
      : t('sensor.door_open_chart.duration_days_hours_minutes', { days: d, hours: h, minutes: m })
  }
  if (h > 0) {
    return showSeconds.value
      ? t('sensor.door_open_chart.duration_hours_minutes_seconds', { hours: h, minutes: m, seconds })
      : t('sensor.door_open_chart.duration_hours_minutes', { hours: h, minutes: m })
  }
  if (showSeconds.value) {
    return t('sensor.door_open_chart.duration_minutes_seconds', { minutes: m, seconds })
  }
  return t('sensor.door_open_chart.duration_minutes', { minutes: totalMin })
}

function formatPeriodDuration(period: IDoorOpenPeriod): string {
  if (showSeconds.value) {
    return formatDuration(period.start_date, period.end_date)
  }
  if (period.duration != null && period.duration !== '') {
    if (typeof period.duration === 'number') {
      const totalMin = period.duration
      const h = Math.floor(totalMin / 60)
      const m = totalMin % 60
      if (h > 0) {
        return t('sensor.door_open_chart.duration_hours_minutes', { hours: h, minutes: m })
      }
      return t('sensor.door_open_chart.duration_minutes', { minutes: totalMin })
    }
    return String(period.duration)
  }
  return formatDuration(period.start_date, period.end_date)
}

/** Duração das aberturas alarmáveis (`open_periods`): sempre em minutos, alinhada ao backend. */
function formatAlarmWorthyPeriodDuration(period: IDoorOpenPeriod): string {
  if (showSeconds.value) {
    return formatDuration(period.start_date, period.end_date)
  }
  const totalMin = periodDurationMinutes(period)
  if (totalMin <= 0) return t('sensor.door_open_chart.duration_empty')
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  if (h > 0) {
    return t('sensor.door_open_chart.duration_hours_minutes', { hours: h, minutes: m })
  }
  return t('sensor.door_open_chart.duration_minutes', { minutes: totalMin })
}

function parseOpenPeriods(res: unknown): IDoorOpenPeriod[] {
  if (!res || typeof res !== 'object') return []
  const periods = (res as DoorOpenPeriodResponse).open_periods
  return Array.isArray(periods) ? periods : []
}

async function loadAlarmProfileSetpoints() {
  if (!props.alarmDoorOpenProfileId) {
    maxDoorOpenTimeSetpoint.value = null
    debounceTimeSetpoint.value = null
    return
  }
  try {
    const profile = await AlarmResource.getAlarmDoorOpenSensorProfile(props.alarmDoorOpenProfileId)
    const settings = (profile?.settings ?? profile) as {
      max_door_open_time_setpoint?: number | null
      debounce_time_setpoint?: number | null
    } | null
    maxDoorOpenTimeSetpoint.value = settings?.max_door_open_time_setpoint ?? null
    debounceTimeSetpoint.value = settings?.debounce_time_setpoint ?? null
  } catch {
    maxDoorOpenTimeSetpoint.value = null
    debounceTimeSetpoint.value = null
  }
  if (currentRange.value) {
    fetchReadings(currentRange.value)
  }
}

function applyPeriodLayers(
  readings: Record<string, unknown>,
  periodsRes: unknown,
  range: TimeRangeValue,
) {
  const rangeEndMs = range.end.getTime()
  const timeDoorOpen = parseTimeDoorOpen(readings)
  const openPeriods = hasDoorOpenAlarmConfig.value ? parseOpenPeriods(periodsRes) : []

  alarmWorthyPeriods.value = openPeriods
  shortOpenPeriods.value = buildShortOpenPeriods(
    timeDoorOpen,
    openPeriods,
    maxDoorOpenTimeSetpoint.value,
    rangeEndMs,
    hasDoorOpenAlarmConfig.value,
  )
}

function openAlarmDetail(alarm: ISensorChartOverlayAlarm) {
  if (alarm.origin === 'group') {
    if (!alarm.group?.id) return
    router.push({
      name: 'group-alarm-detail',
      params: { groupId: String(alarm.group.id), alarmId: String(alarm.id) },
      query: { alarmType: alarm.alarm_type ?? '' },
    })
    return
  }

  router.push({
    name: 'alarm-detail',
    params: {
      sensorId: String(props.sensorId),
      alarmId: String(alarm.id),
    },
    query: { alarmType: alarm.alarm_type ?? '' },
  })
}

function onAlarmHover(alarm: ISensorChartOverlayAlarm | null) {
  hoveredAlarmIndex.value = resolveChartHoverIndex(alarm, sensorAlarms.value)
}

function windowPeriodMinutes(range: TimeRangeValue): number {
  const hours = (range.end.getTime() - range.start.getTime()) / (60 * 60 * 1000)
  return hours > 168 ? 15 : 1
}

const doorOpenChartStore = useDoorOpenChartStore()

async function fetchAlarms(range: TimeRangeValue) {
  if (!props.sensorId) return
  isAlarmsLoading.value = true
  try {
    const data = await doorOpenChartStore.fetchAlarms(
      props.sensorId,
      range.start.toISOString(),
      range.end.toISOString(),
    )
    alarmList.value = data ?? []
  } catch {
    alarmList.value = []
  } finally {
    isAlarmsLoading.value = false
  }
}

async function fetchReadings(range: TimeRangeValue) {
  if (!props.sensorId) return
  isLoading.value = true
  try {
    const startIso = range.start.toISOString()
    const endIso = range.end.toISOString()
    const periodsPromise = hasDoorOpenAlarmConfig.value
      ? doorOpenChartStore.fetchDoorOpenPeriods(props.sensorId, startIso, endIso).catch(() => null)
      : Promise.resolve(null)

    const [readingsResRaw, periodsRes] = await Promise.all([
      doorOpenChartStore.fetchReadings(
        props.sensorId,
        startIso,
        endIso,
        selectedResolution.value,
      ),
      periodsPromise,
      fetchAlarms(range),
    ])

    const res = readingsResRaw as { readings?: Record<string, unknown> }
    const doorOpenReadingsObj = res?.readings ?? {}

    applyPeriodLayers(doorOpenReadingsObj, periodsRes, range)
    savedDataZoom.value = { start: 0, end: 100 }
    hasFetched.value = true
  } catch {
    alarmWorthyPeriods.value = []
    shortOpenPeriods.value = []
    savedDataZoom.value = { start: 0, end: 100 }
    hasFetched.value = true
  } finally {
    isLoading.value = false
  }
}

function onRangeChange(range: TimeRangeValue) {
  currentRange.value = range
  selectedResolution.value = defaultResolutionForRange(range)
  fetchReadings(range)
}

function captureDataZoomState() {
  if (!chartInstance) return
  const opt = chartInstance.getOption() as { dataZoom?: Array<{ start?: number; end?: number }> } | undefined
  const dz = opt?.dataZoom
  if (Array.isArray(dz) && dz.length > 0) {
    const withRange = dz.find((d) => typeof (d as { start?: number; end?: number }).start === 'number' && typeof (d as { start?: number; end?: number }).end === 'number') as { start: number; end: number } | undefined
    if (withRange) savedDataZoom.value = { start: withRange.start, end: withRange.end }
  }
}

function openFullscreen() {
  captureDataZoomState()
  const zoomToRestore = { ...savedDataZoom.value }
  isFullscreen.value = true
  nextTick(() => {
    nextTick(() => {
      savedDataZoom.value = { ...zoomToRestore }
      initChart()
      nextTick(() => chartInstance?.resize())
    })
  })
}

function closeFullscreen() {
  captureDataZoomState()
  isFullscreen.value = false
  nextTick(() => {
    initChart()
    nextTick(() => chartInstance?.resize())
  })
}

function getActiveChartContainer(): HTMLDivElement | null {
  return isFullscreen.value ? chartContainerFullscreen.value : chartContainer.value
}

function initChart() {
  const container = getActiveChartContainer()
  if (!container) return
  if (chartInstance) {
    chartInstance.dispose()
    chartInstance = null
  }
  chartInstance = echarts.init(container)
  updateChart()
}

const { chartOptions } = useSensorDoorOpenChart(
  chartRowLabel,
  doorOpenPeriodsMs,
  rangeStartMs,
  rangeEndMs,
  alarmPeriodsMs,
  hoveredAlarmIndex,
  savedDataZoom,
)

function updateChart() {
  if (!chartInstance) return
  if (!hasChartRange.value) {
    chartInstance.clear()
    return
  }
  chartInstance.setOption(chartOptions.value, true)
}

watch(
  [doorOpenPeriodsMs, alarmPeriodsMs, hoveredAlarmIndex, rangeStartMs, rangeEndMs],
  () => {
    nextTick(() => updateChart())
  },
  { deep: true },
)

watch(
  () => props.alarmDoorOpenProfileId,
  () => {
    loadAlarmProfileSetpoints()
  },
)

watch(isFullscreen, () => {
  nextTick(() => chartInstance?.resize())
})

watch(selectedResolution, () => {
  if (currentRange.value && hasFetched.value && availableResolutions.value.some((o) => o.value === selectedResolution.value)) {
    fetchReadings(currentRange.value)
  }
})

const handleResize = () => chartInstance?.resize()

onMounted(async () => {
  await loadAlarmProfileSetpoints()
  nextTick(() => initChart())
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
  if (chartInstance) {
    chartInstance.dispose()
    chartInstance = null
  }
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>